import Player from '@vimeo/player'

export type VideoEventType = {
  play: () => void
}

export type SproutVideoPlayer = {
  bind: (eventName: string, callback: () => void) => void
  play: () => void
}

declare global {
  interface Window {
    _wq: { id: string; onReady?: (video: { bind: (eventName: string, callback: () => void) => void; play: () => void }) => void }[]
    SV: {
      Player: {
        new (params: { videoId: string }): SproutVideoPlayer
      }
    }
  }
}

window._wq = window._wq || []

function dispatchVideoEvent(eventName: string, element: HTMLElement, data?: VideoEventType) {
  let event: CustomEvent
  if (window.CustomEvent && typeof window.CustomEvent === 'function') {
    event = new CustomEvent(eventName, { detail: data })
  } else {
    event = document.createEvent('CustomEvent')
    event.initCustomEvent(eventName, false, false, data)
  }
  element.dispatchEvent(event)
}

const videos: { [index: string]: Promise<{ html?: string; thumbnail_url?: string; description?: string }> } = {}

const getVideo = (
  url: string,
  id: string | null,
  provider: string | null,
  callback: (video?: { html?: string; thumbnail_url?: string; description?: string }) => void,
  quality?: string | null
) => {
  let oembed_url = ''
  switch (provider) {
    case 'wistia':
      oembed_url = `https://fast.wistia.com/oembed?url=${encodeURIComponent(url)}`
      break
    case 'vimeo':
      oembed_url = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}&width=640&maxwidth=720&maxheight=480&quality=${
        quality || 'auto'
      }`
      break
    case 'sprout':
      oembed_url = `/apps/downloads/sprout_oembed.json?url=${encodeURIComponent(url)}`
      break
  }

  const handleError = () => {
    delete videos[url]
    const streamingVideoElement = document?.querySelector(`#streaming-video-${id}`) as HTMLElement
    const streamingVideoMissingElement = document?.querySelector(`#streaming-video-missing-${id}`) as HTMLElement
    const nativeVideoElement = document?.querySelector(`#fallback-native-video-${id}`) as HTMLElement

    streamingVideoElement.style.display = 'none'
    if (nativeVideoElement) nativeVideoElement.style.display = 'block'
    else streamingVideoMissingElement.style.display = 'block'
  }

  videos[url] =
    videos[url] ||
    // eslint-disable-next-line compat/compat
    fetch(oembed_url, {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          handleError()
        }
        return response.json()
      })
      .catch(() => {
        handleError()
      })

  videos[url]
    .then((data) => {
      callback({ thumbnail_url: data.thumbnail_url, html: data.html, description: data.description })
    })
    .catch(() => {
      handleError()
    })
}

const recordVideoView = (element: HTMLElement, video_id: string, order_item_id: string, order_id: string) => {
  if (!element.getAttribute('data-played')) {
    fetch('/apps/downloads/video_views/' + video_id, {
      method: 'post',
      body: JSON.stringify({ order_item_id: order_item_id, order_id: order_id }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
    element.setAttribute('data-played', 'true')
  }
}

const VimeoVideo = (element: HTMLElement) => {
  const videoUrl = element.getAttribute('data-video-url')
  const provider = element.getAttribute('data-provider')
  const video_id = element.getAttribute('data-video-id')
  const order_item_id = element.getAttribute('data-item-id')
  const order_id = element.getAttribute('data-order-id')
  const quality = element.getAttribute('data-video-quality')

  if (videoUrl) {
    getVideo(
      videoUrl,
      video_id,
      provider,
      (video) => {
        if (video?.html) {
          element.innerHTML = video.html
          const iframe = element.querySelector('iframe')
          if (iframe) {
            const player = new Player(iframe)

            if (video_id && order_item_id && order_id)
              player.on('play', function () {
                recordVideoView(element, video_id, order_item_id, order_id)
              })

            player.on('loaded', function () {
              dispatchVideoEvent('sky-pilot-video-loaded', element, player)
            })

            player.on('ended', function () {
              dispatchVideoEvent('sky-pilot-video-ended', element, player)
            })
          }
        }
      },
      quality
    )
  }
}

const WistiaVideo = (element: HTMLElement) => {
  const videoUrl = element.getAttribute('data-video-url')
  const provider = element.getAttribute('data-provider')
  const video_id = element.getAttribute('data-video-id')
  const wistiaId = element.getAttribute('data-provider-id')
  const order_item_id = element.getAttribute('data-item-id')
  const order_id = element.getAttribute('data-order-id')

  if (videoUrl) {
    //TODO: Use async?
    // getVideo(`${videoUrl}?embedType=async`, provider, (video) => {
    getVideo(videoUrl, video_id, provider, (video) => {
      if (video?.html && wistiaId) {
        element.innerHTML = video.html
        window._wq.push({
          id: wistiaId,
          onReady: (wistiaVideo) => {
            if (video_id && order_item_id && order_id)
              wistiaVideo.bind('play', () => {
                recordVideoView(element, video_id, order_item_id, order_id)
              })

            wistiaVideo.bind('end', () => {
              dispatchVideoEvent('sky-pilot-video-ended', element, wistiaVideo)
            })

            dispatchVideoEvent('sky-pilot-video-loaded', element, wistiaVideo)
          },
        })
      }
    })
  }
}

const SproutVideo = (element: HTMLElement) => {
  const videoUrl = element.getAttribute('data-video-url')
  const provider = element.getAttribute('data-provider')
  const video_id = element.getAttribute('data-video-id')
  const sproutId = element.getAttribute('data-provider-id')
  const order_item_id = element.getAttribute('data-item-id')
  const order_id = element.getAttribute('data-order-id')

  if (videoUrl) {
    getVideo(videoUrl, video_id, provider, (video) => {
      if (video?.html && sproutId) {
        element.innerHTML = video.html
        const player = new window.SV.Player({ videoId: sproutId })

        if (video_id && order_item_id && order_id) {
          player.bind('play', function () {
            recordVideoView(element, video_id, order_item_id, order_id)
          })

          player.bind('ready', function () {
            dispatchVideoEvent('sky-pilot-video-loaded', element, player)
          })

          player.bind('completed', function () {
            dispatchVideoEvent('sky-pilot-video-ended', element, player)
          })
        }
      }
    })
  }
}

const CloudinaryVideo = (element: HTMLVideoElement) => {
  const video_id = element.getAttribute('data-video-id')
  const order_item_id = element.getAttribute('data-item-id')
  const order_id = element.getAttribute('data-order-id')

  element.querySelectorAll('source').forEach((source) => {
    const url = source.getAttribute('src')
    const errorDiv = document.getElementById('video-source-error-' + video_id)

    function fetchRequest() {
      if (!url) return

      // eslint-disable-next-line compat/compat
      fetch(url, { method: 'HEAD' })
        .then((response) => {
          if (response.ok) {
            element.load()
            errorDiv?.style.setProperty('display', 'none')
            element.style.setProperty('display', 'block')
            clearInterval(intervalId)
          } else {
            errorDiv?.style.setProperty('display', 'block')
            element.style.setProperty('display', 'none')
          }
        })
        .catch(() => {
          errorDiv?.style.setProperty('display', 'block')
          element.style.setProperty('display', 'none')
        })
    }

    fetchRequest()
    const intervalId = setInterval(fetchRequest, 20000)
  })

  if (video_id && order_id && order_item_id) {
    element.addEventListener('play', function () {
      recordVideoView(element, video_id, order_item_id, order_id)
    })

    element.addEventListener('contextmenu', function (e) {
      e.preventDefault()
    })
  }
}

export const initializeVideo = (element: HTMLElement) => {
  const provider = element.getAttribute('data-provider')
  switch (provider) {
    case 'vimeo':
      VimeoVideo(element)
      break

    case 'wistia':
      WistiaVideo(element)
      break

    case 'sprout':
      SproutVideo(element)
      break
    case 'cloudinary':
      if (element instanceof HTMLVideoElement) {
        CloudinaryVideo(element as HTMLVideoElement)
      }
      break
  }
}

export const initializeVideoThumbnail = (element: HTMLElement) => {
  const videoUrl = element.getAttribute('data-video-url')
  const provider = element.getAttribute('data-provider')
  const video_id = element.getAttribute('data-video-id')
  if (videoUrl)
    getVideo(videoUrl, video_id, provider, (video) => {
      if (video?.thumbnail_url) element.setAttribute('src', video.thumbnail_url)
    })
}

export const initializeVideoDescription = (element: HTMLElement) => {
  const provider = element.getAttribute('data-provider')
  if (provider === 'vimeo') {
    const video_id = element.getAttribute('data-video-id')
    const videoUrl = element.getAttribute('data-video-url')
    if (videoUrl)
      getVideo(videoUrl, video_id, provider, (video) => {
        if (video?.description) {
          const descriptionElement = document.getElementById('sky-pilot-embed-description-' + video_id)
          if (descriptionElement) descriptionElement.innerHTML = video.description.replace(/(?:\r\n|\r|\n)/g, '<br/>')
        }
      })
  }
}

export const initializeVideoThumbnails = (parent?: HTMLElement) => {
  const scope = parent || document

  scope.querySelectorAll<HTMLElement>('.sky-pilot-thumbnail').forEach((el) => {
    initializeVideoThumbnail(el)
  })
}

export const initializeVideoDescriptions = (parent?: HTMLElement) => {
  const scope = parent || document

  scope.querySelectorAll<HTMLElement>('.sky-pilot-embed-description-remote').forEach((el) => {
    initializeVideoDescription(el)
  })
}

export const initializeVideos = (parent?: HTMLElement) => {
  const scope = parent || document
  scope.querySelectorAll<HTMLElement>('.sky-pilot-embed').forEach((el) => {
    initializeVideo(el)
  })
}
